/**
 * The live preview on the main thread (decisions 10, 12, 61; part 15, table M): what the layout worker is asked and
 * when. While the preview is shown, the open chapter goes to the worker once and then each edit as its changes; the
 * pages around the cursor are asked for after every change of the text or the cursor, one request at a time, so the
 * worker never queues up states the writer has typed past; once the writer pauses, the worker lays the chapter out to its
 * end so page numbers settle. A preview that isn't shown asks for nothing (decision 12): its edits are dropped, and the
 * chapter goes to the worker again when it is shown.
 *
 * The preview is a book on every profile (decision 70; part 15a, table M rows M17–M27; part 15b): spreads on the facing
 * profile, else one page at a time, across chapters. It follows the cursor until the writer turns; then it has a place of its own,
 * until an edit or "back to the cursor" brings it back. The chapters beside the shown one are read from the store and
 * laid out read-only in the worker (its shelf), so browsing never reopens the open chapter.
 *
 * The worker connection and the timer are injected, so the logic runs in tests without a worker.
 */
import type { EditorUpdate } from '@cockatrice/editor';
import { MAX_TURN } from '@cockatrice/layout';
import type { Bitmap, BookMode, BookSlot, BookView, HeldBookDraw, LayoutClient, Place, PreviewSettings, ShelfChapter } from '@cockatrice/layout';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';

/** CU11: how many times a close-up is asked for while other messages overtake it in the worker. */
export const CLOSE_UP_TRIES = 8;

/** M8: how long the text and cursor stay still before the worker settles the chapter's page numbers. */
export const IDLE_MS = 750;

/** The chapter the preview shows: the editor holding it, its settings, and its text and cursor as they are now. */
export interface PreviewDocument {
  /** The open editor; another editor (a chapter opened, or opened again) is another document (M10). */
  readonly editor: object;
  readonly settings: PreviewSettings;
  readonly text: () => string;
  readonly cursor: () => number;
}

/** A unit of the book view (part 15a): its slots as the worker gave them, and its place (U4), null with no page or blank. */
export interface ShownUnit {
  readonly slots: readonly BookSlot[];
  readonly place: Place | null;
}

/** A page drawn for the close-up (part 15b): its pixels and the text they show. */
export interface CloseUpPage {
  readonly bitmap: Bitmap;
  readonly text: string | null;
}

/** What the preview shows (M-rows' outcomes). */
export type PreviewState =
  | { readonly kind: 'none' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed' }
  /**
   * M17–M27: the book view: the shown unit (`current`) among the window's; `away` while it has a place of its own; `start`
   * and `end` when nothing lies before or after the shown unit.
   */
  | { readonly kind: 'book'; readonly units: readonly ShownUnit[]; readonly current: number; readonly away: boolean; readonly start: boolean; readonly end: boolean };

/** A chapter of the open book as the book view knows it: its ID and the settings it is laid out with (its number its place). */
export interface BookChapterEntry {
  readonly id: string;
  readonly settings: PreviewSettings;
}

/**
 * The open book for the book view (M24): its live chapters in order, which reading of the book this is (SH9: another tab
 * may have changed any chapter between two), and how a chapter's stored text is read (null when it can't be).
 */
export interface PreviewBook {
  readonly chapters: readonly BookChapterEntry[];
  readonly reading: number;
  readonly read: (chapterId: string) => Promise<string | null>;
}

/** A started layout worker: its client, and what stops it. */
export interface LayoutConnection {
  readonly client: LayoutClient;
  terminate(): void;
}

/** What the controller needs; everything with an effect is injected. */
export interface PreviewOptions {
  /** Starts a layout worker; called the first time the preview has something to show, and again after a restart. */
  readonly connect: () => LayoutConnection;
  /** Runs `run` after `ms` milliseconds; returns what cancels it. */
  readonly schedule: (ms: number, run: () => void) => () => void;
}

/** Whether two documents are the same: one editor, with settings that lay it out alike (M9, M10). */
export function sameDocument(a: PreviewDocument | null, b: PreviewDocument | null): boolean {
  if (a === null || b === null) return a === b;
  return a.editor === b.editor && JSON.stringify(a.settings) === JSON.stringify(b.settings);
}

/**
 * M23, SH9: whether two books list the same chapters with the same settings, from the same reading of the book (a reader
 * is the store's, whichever it is).
 */
export function sameBook(a: PreviewBook | null, b: PreviewBook | null): boolean {
  if (a === null || b === null) return a === b;
  return a.reading === b.reading && JSON.stringify(a.chapters) === JSON.stringify(b.chapters);
}

/** The book view's shown unit, or null in any other state (or with `current` out of range, which V12 refuses). */
export function shownUnit(state: PreviewState): ShownUnit | null {
  return state.kind === 'book' ? (state.units[state.current] ?? null) : null;
}

/**
 * Part 15b, CH7: the chapter the chapter buttons go from: that of the shown unit's last page or blank, or null with none
 * shown. The last, not the first: a spread can hold one chapter's end and the next one's start, and a jump to that next
 * chapter must count as being there.
 */
export function shownChapter(state: PreviewState): number | null {
  const slots = shownUnit(state)?.slots ?? [];
  return slots.flatMap((slot) => (slot.kind === 'page' || slot.kind === 'blank' ? [slot.chapter] : [])).at(-1) ?? null;
}

/** TL9: turns waiting, at most {@link MAX_TURN} either way, as many as a request carries (V11). */
export function clampTurn(turn: number): number {
  return Math.max(-MAX_TURN, Math.min(MAX_TURN, turn));
}

/** Table SH: a set of chapters wanted on the shelf (as JSON, and each), and the book, document and session it is for. */
interface Want {
  readonly key: string;
  readonly chapters: readonly BookChapterEntry[];
  readonly book: PreviewBook;
  readonly document: PreviewDocument;
  readonly session: number;
}

/** U4: a unit's place: its first page or blank. */
export function unitPlace(slots: readonly BookSlot[]): Place | null {
  for (const slot of slots) {
    if (slot.kind === 'page') return { chapter: slot.chapter, page: slot.index };
    if (slot.kind === 'blank') return { chapter: slot.chapter, page: -1 };
  }
  return null;
}

/** M24: the chapters the shelf holds beside a unit of chapter `chapter`: the one before, it and the one after, in the book, not the open one. */
export function shelfNumbers(chapter: number, count: number, open: number): number[] {
  return [chapter - 1, chapter, chapter + 1].filter((number) => number >= 1 && number <= count && number !== open);
}



/** The preview's requests to the layout worker. */
export class PreviewController {
  readonly state: Accessor<PreviewState>;
  readonly #setState: (state: PreviewState) => void;
  readonly #options: PreviewOptions;
  #connection: LayoutConnection | null = null;
  #document: PreviewDocument | null = null;
  #visible = false;
  #scale: number | null = null;
  /** The worker session the document is open under: a new number for each document, and after a restart. */
  #session = 0;
  /** M2: whether the worker holds the document's text as it is now (it was opened, and every edit since was sent). */
  #opened = false;
  #viewing = false;
  /** M4, M5: the text or cursor changed since the last view was asked for. */
  #dirty = false;
  /** H6: the next view holds nothing, so the worker draws every page again. */
  #forget = false;
  #failed = false;
  #idle: (() => void) | null = null;
  /** M24: the open book, for the book view. */
  #book: PreviewBook | null = null;
  /** M19: one page or spreads, from the pane's width. */
  #mode: BookMode = 'spread';
  /** M18, M19: the book view's own place, or null while it follows the cursor. */
  #place: Place | null = null;
  /** M19: turns asked for and not yet sent. */
  #turn = 0;
  /** M21–M23: a new number each time the place is given back to the cursor, so an answer asked for before is let go. */
  #following = 0;
  /**
   * M24: the chapters last wanted on the worker's shelf this session (their IDs and settings, as JSON), so a shelf is
   * read and sent once for each set, a chapter that can't be read included.
   */
  #wanted: string | null = null;
  /** M24, table SH: whether a shelf pass is reading, and the set wanted last (by the latest answer), with what it is for. */
  #shelving = false;
  #want: Want | null = null;

  constructor(options: PreviewOptions) {
    this.#options = options;
    const [state, setState] = createSignal<PreviewState>({ kind: 'none' });
    this.state = state;
    this.#setState = setState;
  }

  /** M10, M13: the chapter to show, or null; a document the same as the one shown changes nothing. */
  setDocument(document: PreviewDocument | null): void {
    if (sameDocument(document, this.#document)) return;
    this.#closeSession();
    this.#document = document;
    this.#follow();
    this.#rest();
    this.#refresh();
  }

  /** M23, M24: the open book's chapters for the book view; another listing gives the place back to the cursor. */
  setBook(book: PreviewBook | null): void {
    if (sameBook(book, this.#book)) return;
    this.#book = book;
    // SH9 (deep-fix r4): another listing or another reading of the book: the shelf's texts are read again.
    this.#wanted = null;
    this.#follow();
    this.#refresh();
  }

  /**
   * M23 (revised in part 15b): spreads or one page at a time (the pane's width, or the writer's button). The place stays:
   * pressing One page on a page browsed to shows that page. Turns waiting, and answers on their way, were for the old mode.
   */
  setMode(mode: BookMode): void {
    if (mode === this.#mode) return;
    this.#mode = mode;
    this.#turn = 0;
    this.#following += 1;
    this.#refresh();
  }

  /**
   * M19, M20: turns the book view by `step` units. A neighbour the window holds shows at once; the turn goes to the
   * worker either way, adding to any not yet answered. Nothing past the book's start or end, or outside the book view.
   */
  turn(step: 1 | -1): void {
    const state = this.state();
    if (state.kind !== 'book' || (step === 1 ? state.end : state.start)) return;
    // M19: the place becomes the shown unit's; it is its own from now on. A unit with no page or blank has none to turn from.
    this.#place ??= shownUnit(state)?.place ?? null;
    if (this.#place === null) return;
    // TL9: the turns waiting are at most a request's (V11); presses past it, held keys' repeats, turn no further.
    this.#turn = clampTurn(this.#turn + step);
    const next = state.current + step;
    if (next >= 0 && next < state.units.length) this.#setState({ ...state, current: next, away: true, start: false, end: false });
    else this.#setState({ ...state, away: true });
    this.#refresh();
  }

  /**
   * Part 15b, table CH: to the first page of the chapter `step` from the shown one, without touching the editor. The
   * target may not be laid out yet: its unit shows as pending until the shelf holds it. Nothing past the book's first or
   * last chapter, or with no page shown to go from.
   */
  chapter(step: 1 | -1): void {
    const state = this.state();
    if (state.kind !== 'book') return;
    const from = shownChapter(state);
    const count = this.#book?.chapters.length ?? 0;
    if (from === null || from + step < 1 || from + step > count) return;
    this.#place = { chapter: from + step, page: 0 };
    this.#turn = 0;
    this.#following += 1;
    this.#setState({ ...state, away: true });
    this.#refresh();
  }

  /**
   * Part 15b, table CU: a page drawn apart at `scale` for the close-up, leaving the book view's place and turns alone.
   * CU11: a stale answer only means another message reached the worker (a shelf, a settle, the view), so it is asked again
   * while the session lasts, a few times. CU12: it may have overtaken a settle or a view, so the view is asked for again,
   * which sets the settle going again as any view does; CU13: `apart` keeps what the view holds, so that redraws nothing,
   * and a focused Enlarge stays in the page. Null when the
   * worker refused, the session ended, or it stayed overtaken.
   */
  async closeUp(place: Place, scale: number): Promise<CloseUpPage | null> {
    const document = this.#document;
    if (document === null || !this.#opened || this.#failed) return null;
    const session = this.#session;
    const chapters = Math.max(this.#book?.chapters.length ?? 0, document.settings.number);
    let view: BookView | null = null;
    for (let tries = 0; view === null && tries < CLOSE_UP_TRIES && session === this.#session; tries += 1) {
      const answer = await this.#client()
        .spread({ session, cursor: document.cursor(), place, turn: 0, mode: 'page', chapters, scale, held: [], apart: true })
        .then(
          (answered) => ({ answered }),
          () => null,
        );
      if (answer === null) break;
      view = answer.answered;
    }
    this.#refresh();
    const slot = view?.units[view.current]?.[0];
    return slot?.kind === 'page' && slot.bitmap !== null ? { bitmap: slot.bitmap, text: slot.text } : null;
  }

  /** M22: back to the cursor's unit. */
  backToCursor(): void {
    if (this.#place === null) return;
    this.#follow();
    this.#refresh();
  }

  /** M1, M2, M14: whether the preview is shown; a hidden one asks for nothing and forgets the pages it showed. */
  setVisible(visible: boolean): void {
    if (visible === this.#visible) return;
    this.#visible = visible;
    if (!visible) this.#rest();
    this.#refresh();
  }

  /** M15: the canvas pixels per point pages are drawn at, or null while the pane has no width. */
  setScale(scale: number | null): void {
    if (scale === this.#scale) return;
    this.#scale = scale;
    this.#refresh();
  }

  /** M4, M5, M11: a change of `editor`'s text or cursor. */
  edited(editor: object, update: EditorUpdate): void {
    const document = this.#document;
    if (document?.editor !== editor) return;
    if (update.changes.length > 0) {
      // M11: a change the worker doesn't get leaves its text behind; the document goes to it again when it is needed.
      if (this.#opened && this.#visible && !this.#failed) this.#sendEdit(this.#session, update);
      else this.#opened = false;
      // M21: typing brings the book view back to the cursor.
      this.#follow();
    } else if (this.#place !== null) {
      // M21: a cursor move alone leaves a book view that has a place of its own where it is.
      return;
    }
    this.#refresh();
  }

  /** M16: starts a new layout worker after a failure, and shows the document again. */
  restart(): void {
    this.#connection?.terminate();
    this.#connection = null;
    this.#failed = false;
    this.#session += 1;
    this.#opened = false;
    this.#wanted = null;
    this.#rest();
    this.#refresh();
  }

  /** Stops the worker (the app closing). */
  stop(): void {
    this.#cancelIdle();
    this.#connection?.terminate();
    this.#connection = null;
    this.#document = null;
  }

  /** The worker's client, starting the worker the first time one is needed. */
  #client(): LayoutClient {
    this.#connection ??= this.#options.connect();
    return this.#connection.client;
  }

  /** M10, M13: closes the document's session in the worker; the next document opens under a new number. */
  #closeSession(): void {
    if (this.#opened && !this.#failed) {
      // The worker keeps nothing for a closed session; whether it answers changes nothing here.
      this.#client()
        .close(this.#session)
        .catch(() => undefined);
    }
    this.#session += 1;
    this.#opened = false;
    // Q22: the worker's shelf goes with the session, so the next reads its chapters afresh (one the writer has edited
    // since included: deep-fix r1).
    this.#wanted = null;
  }

  /** M18, M21–M23: the book view follows the cursor again; answers asked for before are let go. */
  #follow(): void {
    this.#place = null;
    this.#turn = 0;
    this.#following += 1;
  }

  /** What the preview shows with no pages: nothing to show, being set, or failed; held drawings are let go. */
  #rest(): void {
    this.#cancelIdle();
    this.#setState(this.#failed ? { kind: 'failed' } : this.#document === null ? { kind: 'none' } : { kind: 'loading' });
  }

  #cancelIdle(): void {
    this.#idle?.();
    this.#idle = null;
  }

  /** M2–M5: asks for the pages around the cursor, opening the document first when the worker doesn't hold it. */
  #refresh(): void {
    this.#cancelIdle();
    const document = this.#document;
    if (!this.#visible || document === null || this.#scale === null || this.#failed) return;
    if (!this.#opened) {
      this.#opened = true;
      const session = this.#session;
      this.#client()
        .open(session, document.text(), document.settings)
        .catch(() => {
          this.#fail(session);
        });
    }
    this.#dirty = true;
    if (this.#viewing) return;
    this.#spread(document, this.#scale);
  }

  /** M11: sends an edit; a worker that can't apply it gets the whole text again (M12). */
  #sendEdit(session: number, update: EditorUpdate): void {
    this.#client()
      .edit(session, update.changes)
      .catch(() => {
        if (session !== this.#session) return;
        this.#opened = false;
        this.#refresh();
      });
  }

  /** After a view: another if something changed, else the idle settle while the page numbers are estimates (M7, M8). */
  #next(unsettled = false): void {
    if (this.#dirty) {
      this.#refresh();
      return;
    }
    // Shown pages mean the preview is visible and hasn't failed: hiding and failing both rest it (#rest).
    const state = this.state();
    if (state.kind === 'book' && unsettled) {
      const session = this.#session;
      this.#idle = this.#options.schedule(IDLE_MS, () => {
        this.#idle = null;
        this.#settle(session);
      });
    }
  }

  /**
   * M17–M27: one book view request: the window around the place (or the cursor) after the turns not yet sent; when
   * answered, the shelf is brought up to date, then another request if anything changed meanwhile, else the idle settle.
   */
  #spread(document: PreviewDocument, scale: number): void {
    this.#viewing = true;
    this.#dirty = false;
    const session = this.#session;
    const following = this.#following;
    const turn = this.#turn;
    this.#turn = 0;
    const held = this.#forget ? [] : this.#heldBook();
    this.#forget = false;
    const chapters = Math.max(this.#book?.chapters.length ?? 0, document.settings.number);
    this.#client()
      .spread({ session, cursor: document.cursor(), place: this.#place, turn, mode: this.#mode, chapters, scale, held })
      .then(
        (view) => {
          this.#viewing = false;
          const current = session === this.#session && this.#visible && !this.#failed;
          const same = session === this.#session && following === this.#following;
          // Table TL: a request whose answer isn't shown, for the place it was asked from (overtaken in the worker, TL2;
          // hidden meanwhile, TL6), asks for its turns again.
          if (same && (view === null || !current)) this.#turn = clampTurn(this.#turn + turn);
          // TL3: an answer for a place since given back to the cursor shows nothing; the next request follows.
          if (current && (!same || view === null)) this.#dirty = true;
          else if (current && view !== null) {
            this.#showBook(view);
            this.#shelve(document, view);
          }
          this.#next(view?.unsettled ?? false);
        },
        () => {
          this.#viewing = false;
          this.#fail(session);
          this.#next();
        },
      );
  }

  /** M19: shows a book view answer; the place becomes the shown unit's once the writer has turned. */
  #showBook(view: BookView): void {
    const state = this.state();
    const before = state.kind === 'book' ? state.units.flatMap((unit) => unit.slots) : [];
    const units = view.units.map((slots): ShownUnit => {
      // A held page keeps its object (and canvas); every other slot is a new one, never shared between two sides.
      const kept = slots.map((slot): BookSlot => (slot.kind === 'page' ? (before.find((shown) => shown.kind === 'page' && shown.draw === slot.draw) ?? slot) : { ...slot }));
      return { slots: kept, place: unitPlace(kept) };
    });
    // M27, H6: a page the worker says is held, but isn't here, shows nothing: the next request asks for every page again.
    if (units.some((unit) => unit.slots.some((slot) => slot.kind === 'page' && slot.bitmap === null && !before.includes(slot)))) {
      this.#forget = true;
      this.#dirty = true;
    }
    const away = this.#place !== null;
    // TL10 (deep-fix r2): an answer with no place to name (its unit all pending) leaves the place where it was.
    if (away && view.place !== null) this.#place = view.place;
    this.#setState({ kind: 'book', units, current: view.current, away, start: view.start, end: view.end });
  }

  /** M27: the book view's drawings shown now, by chapter and index, which the worker need not send again. */
  #heldBook(): HeldBookDraw[] {
    const state = this.state();
    if (state.kind !== 'book') return [];
    return state.units.flatMap((unit) => unit.slots.flatMap((slot) => (slot.kind === 'page' ? [{ chapter: slot.chapter, index: slot.index, draw: slot.draw }] : [])));
  }

  /**
   * M24, table SH: notes the chapters wanted beside the shown unit's (SH5) and, unless a pass is reading or the worker's
   * shelf holds them already (SH6), starts a pass for them.
   */
  #shelve(document: PreviewDocument, view: BookView): void {
    const book = this.#book;
    if (book === null) return;
    // Deep-fix r3: the shelf goes where the view is: the answer's place, else the place it was asked from (an answer with
    // none is a unit whose chapter the shelf lacks, after a restart's empty shelf among others), else the open chapter.
    const chapter = view.place?.chapter ?? this.#place?.chapter ?? document.settings.number;
    const numbers = shelfNumbers(chapter, book.chapters.length, document.settings.number);
    const chapters = book.chapters.filter((_, index) => numbers.includes(index + 1));
    this.#want = { key: JSON.stringify(chapters), chapters, book, document, session: this.#session };
    if (!this.#shelving) this.#pass();
  }

  /**
   * Table SH: one shelf pass: the set wanted now, read from the store (SH7: a chapter that can't be read left out), then
   * sent only if it is still the set wanted (SH2: a set wanted meanwhile gets a pass of its own), for the same session,
   * book and document, shown and working (SH1); otherwise the next answer plans again (SH3, SH4).
   */
  #pass(): void {
    const want = this.#want;
    if (want === null || want.key === this.#wanted) return;
    this.#shelving = true;
    void this.#fill(want.book, want.chapters).then((chapters) => {
      this.#shelving = false;
      if (want !== this.#want) {
        this.#pass();
        return;
      }
      const current = want.session === this.#session && want.book === this.#book && want.document === this.#document && this.#visible && !this.#failed;
      if (!current) return;
      this.#wanted = want.key;
      this.#sendShelf(want.session, chapters);
    });
  }

  /** M24: the wanted chapters with their texts as stored now (none when it can't be read). */
  async #fill(book: PreviewBook, wanted: readonly BookChapterEntry[]): Promise<ShelfChapter[]> {
    const chapters: ShelfChapter[] = [];
    for (const entry of wanted) {
      const text = await book.read(entry.id).catch(() => null);
      if (text === null) continue;
      chapters.push({ settings: entry.settings, text });
    }
    return chapters;
  }

  /** M24: sends the shelf; once the worker holds it, the window is asked for again. */
  #sendShelf(session: number, chapters: readonly ShelfChapter[]): void {
    this.#client()
      .shelf(session, chapters)
      .then(
        () => {
          if (session !== this.#session) return;
          this.#dirty = true;
          this.#refresh();
        },
        () => {
          this.#fail(session);
        },
      );
  }

  /** M8: settles the page numbers; once complete, the pages are asked for again with them. */
  #settle(session: number): void {
    this.#client()
      .settle(session)
      .then(
        (complete) => {
          if (complete === true && session === this.#session) this.#refresh();
        },
        () => {
          this.#fail(session);
        },
      );
  }

  /** M13: a failure of the worker for the current session; the preview says so until it is restarted. */
  #fail(session: number): void {
    if (session !== this.#session || this.#failed) return;
    this.#failed = true;
    this.#rest();
  }
}
